/**
 * Tutors {@link DataTable} for the management page.
 * List + filters + expanded details; assignment / reassignment / delete live in modals.
 */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { TutorRow } from './TutorRow';
import { TutorCard } from './TutorCard';
import { TutorFiltersContent } from './TutorFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderTutorDetails } from './renderTutorDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { AssignmentDecisionModal } from './AssignmentDecisionModal';
import { ReassignmentModal } from './ReassignmentModal';
import { useDataTable } from '@/hooks/useDataTable';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useDepartments } from '@/hooks/useDepartments';
import { useListInPersonTutors, useGetInPersonTutorFullState } from '@/hooks/useUsers';
import { exportExcel } from '@/services/inperson/tutors.service';
import { deleteUser } from '@/services/users.service';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { cn } from '@/lib/utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import { useAuthStore } from '@/stores/authStore';
import {
  DEFAULT_TUTOR_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  TUTOR_INPERSON_BACKEND_TAB_MAP,
  type FilterGroupId,
  type TutorInPersonTabView,
} from '@/components/tutors/tutor-configs';
import type { TutorFilters, TutorRecord } from '@/types/inperson/tutor.types';
import {
  availabilityKeysToDays,
  getTutorDeleteItemName,
  getTutorDepartments,
  getTutorExpandedColSpan,
  getTutorReassignDepartmentIds,
  hasTutorActiveFilters,
  mapTutorAssignmentWishes,
  resolveTutorAssignmentModalMode,
  resolveTutorExpandedUserId,
  toTutorApiFilterParams,
  type TutorDataTablePageActions,
} from './tutor-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link TutorsDataTable}. */
interface TutorsDataTableProps {
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: ElementWidth;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditTutor?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: TutorFilters;
  showCreateButton?: boolean;
  onCreateTutor?: () => void;
  isSection?: boolean;
  pageActions?: TutorDataTablePageActions;
  activeTab?: TutorInPersonTabView;
  onTabChange?: (tab: TutorInPersonTabView) => void;
  toolbarLeft?: React.ReactNode;
  companyId?: string;
}

/** Column headers that change with {@link TutorInPersonTabView}. */
function TutorInPersonTableHead({ activeTab }: Readonly<{ activeTab: TutorInPersonTabView }>) {
  if (activeTab === 'tutors') {
    return (
      <tr>
        <th>Nom</th>
        <th>Contact</th>
        <th>Départements</th>
        <th>Statut</th>
        <th>Date d'entrée</th>
        <th>Ancienneté</th>
        <th>Coordinateur</th>
        <th>Nb tutorats</th>
        <th className="w-20 text-center">Actions</th>
      </tr>
    );
  }
  if (activeTab === 'pre_registered') {
    return (
      <tr>
        <th>Nom</th>
        <th>Contact</th>
        <th>Statut</th>
        <th>Progression</th>
        <th>Étapes</th>
        <th>Départements</th>
        <th className="w-20 text-center">Actions</th>
      </tr>
    );
  }
  if (activeTab === 'to_validate') {
    return (
      <tr>
        <th>Bénévole</th>
        <th>Départements</th>
        <th>Recruteur</th>
        <th>Coordinateur</th>
        <th className="w-20 text-center">Actions</th>
      </tr>
    );
  }
  return (
    <tr>
      <th>Bénévole</th>
      <th>Départements</th>
      <th>Recruteur</th>
      <th>Coordinateur</th>
      <th>Motif refus</th>
      <th className="w-20 text-center">Actions</th>
    </tr>
  );
}

/** Tutors {@link DataTable} for the management page. */
export const TutorsDataTable: React.FC<TutorsDataTableProps> = ({
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth: layoutWidthProp = 'full',
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditTutor,
  enableFilters = true,
  defaultFilters = DEFAULT_TUTOR_FILTERS,
  showCreateButton = false,
  onCreateTutor,
  isSection = false,
  pageActions,
  activeTab = 'tutors',
  onTabChange: _onTabChange,
  toolbarLeft,
  companyId: companyIdProp = '',
}) => {
  const { user: authUser } = useAuthStore();
  const companyId = companyIdProp || authUser?.companyId || '';
  const queryClient = useQueryClient();
  const { data: departments = [] } = useDepartments('FR');
  const showExport = pageActions?.canExportCsv === true;
  const [isExporting, setIsExporting] = React.useState(false);

  /** Validate / pre-validate modal payload (`null` when closed). */
  type AssignmentModalState = {
    open: boolean;
    mode: 'pre-validate' | 'validate';
    record: TutorRecord;
  } | null;
  const [assignmentModal, setAssignmentModal] = React.useState<AssignmentModalState>(null);

  /** Reassignment modal payload for the to-reassign tab (`null` when closed). */
  type ReassignModalState = {
    open: boolean;
    record: TutorRecord;
  } | null;
  const [reassignModal, setReassignModal] = React.useState<ReassignModalState>(null);

  const dt = useDataTable<TutorFilters, FilterGroupId>({
    tableName: 'tutors',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  /** Backend tab key sent to the list API. */
  const backendTabView = TUTOR_INPERSON_BACKEND_TAB_MAP[activeTab];
  /** Availability filter keys mapped to API day values. */
  const availabilityDays = availabilityKeysToDays(dt.filters.availability);

  /** Previous tab — skip the effect on mount (and React 18 StrictMode double-invoke). */
  const prevActiveTabRef = React.useRef(activeTab);
  /** Reset to page 1 and clear status chips when the tab actually changes. */
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.setCurrentPage(1);
    dt.setFilters((f) => ({ ...f, statuses: [] }));
  }, [activeTab, dt.setCurrentPage, dt.setFilters]);

  const { data: apiResponse, isLoading } = useListInPersonTutors({
    ...toTutorApiFilterParams(dt.filters, availabilityDays, backendTabView ?? undefined),
    page: dt.currentPage,
    pageSize: dt.itemsPerPage,
  });

  /** Current page slice from the list API. */
  const data: TutorRecord[] = apiResponse?.data ?? [];
  const totalItems = apiResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  /** Disable export when the current filter set matches no tutors. */
  const isEmptyForExport = !isLoading && totalItems === 0;

  const expandedUserId = resolveTutorExpandedUserId(expandedRowId, data);

  /** Fetch expanded tutor full state. */
  const {
    data: expandedTutorFullState,
    isLoading: isLoadingExpandedTutor,
    isError: isExpandedTutorError,
    error: expandedTutorError,
  } = useGetInPersonTutorFullState(expandedUserId ?? '', companyId, {
    enabled: !!expandedUserId && !!expandedRowId && !!companyId,
  });
  useUnitGetNotFound({
    isError: isExpandedTutorError,
    error: expandedTutorError,
    catalogKey: 'tutorNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  /** Expanded row record from full-state only. */
  const expandedItem =
    (expandedTutorFullState as { record?: TutorRecord } | undefined)?.record ?? null;

  /** True when any chip / date / establishment filter is set. */
  const hasActiveFilters = React.useMemo(() => hasTutorActiveFilters(dt.filters), [dt.filters]);

  /** Restore default filters and jump back to page 1. */
  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  /** Download the current filter set as Excel. */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportExcel(
        toTutorApiFilterParams(dt.filters, availabilityDays, backendTabView ?? undefined),
      );
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [availabilityDays, backendTabView, dt.filters, isEmptyForExport]);

  /** Soft-delete the tutor then refresh the list. */
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tutors'] });
      toast.success('Tuteur supprimé avec succès !');
      dt.setDeleteId(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Record targeted by the delete confirmation, if any. */
  const recordToDelete = React.useMemo(
    () => data.find((r) => r.userId === dt.deleteId),
    [data, dt.deleteId],
  );

  /** Reassign tab opens {@link ReassignmentModal}; other tabs open {@link AssignmentDecisionModal}. */
  const handleManageAssignment = React.useCallback(
    (record: TutorRecord) => {
      if (activeTab === 'to_reassign') {
        setReassignModal({ open: true, record });
        return;
      }
      setAssignmentModal({
        open: true,
        mode: resolveTutorAssignmentModalMode(pageActions),
        record,
      });
    },
    [activeTab, pageActions],
  );

  /** Close the assignment decision modal. */
  const handleAssignmentModalClose = React.useCallback(() => {
    setAssignmentModal(null);
  }, []);

  /** Refresh the list after a successful assignment decision. */
  const handleAssignmentSuccess = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tutors'] });
    toast.success('Affectation mise à jour avec succès !');
  }, [queryClient]);

  /** Close the reassignment modal. */
  const handleReassignModalClose = React.useCallback(() => {
    setReassignModal(null);
  }, []);

  /** Refresh the list after a successful reassignment. */
  const handleReassignSuccess = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tutors'] });
    toast.success('Réaffectation effectuée avec succès !');
  }, [queryClient]);

  /** Toggle a situation chip in the status filter. */
  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Toggle a subject chip. */
  const toggleSubjectFilter = React.useCallback(
    (subjectId: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        subjects: prev.subjects.includes(subjectId)
          ? prev.subjects.filter((s) => s !== subjectId)
          : [...prev.subjects, subjectId],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Set or clear the establishment filter. */
  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDepartmentCodes = React.useCallback(
    (codes: string[] | undefined) => {
      dt.setFilters((prev) => ({ ...prev, departmentCodes: codes }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Toggle a weekday chip in the availability filter. */
  const toggleAvailability = React.useCallback(
    (day: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        availability: prev.availability.includes(day)
          ? prev.availability.filter((d) => d !== day)
          : [...prev.availability, day],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Engagement-start date range (`from` / `to`). */
  const setEngagementStartDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        engagementStartDateFrom: from,
        engagementStartDateTo: to,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Engagement-end date range (`from` / `to`). */
  const setEngagementEndDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        engagementEndDateFrom: from,
        engagementEndDateTo: to,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Exit date range (`from` / `to`). */
  const setExitDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitDateFrom: from, exitDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Exit-reason select (`undefined` = all). */
  const setExitReason = React.useCallback(
    (reason: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitReason: reason }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Memoized filter panel body (avoids remounting {@link Autocomplete}). */
  const filtersContent = React.useMemo(
    () => (
      <TutorFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        activeTab={activeTab}
        onSetSearch={setSearch}
        onToggleStatus={toggleStatusFilter}
        onToggleSubject={toggleSubjectFilter}
        onSetEstablishment={setEstablishment}
        onSetDepartmentCodes={setDepartmentCodes}
        onToggleAvailability={toggleAvailability}
        onSetEngagementStartDateRange={setEngagementStartDateRange}
        onSetEngagementEndDateRange={setEngagementEndDateRange}
        onSetExitDateRange={setExitDateRange}
        onSetExitReason={setExitReason}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      activeTab,
      setSearch,
      toggleStatusFilter,
      toggleSubjectFilter,
      setEstablishment,
      setDepartmentCodes,
      toggleAvailability,
      setEngagementStartDateRange,
      setEngagementEndDateRange,
      setExitDateRange,
      setExitReason,
    ],
  );

  /** Single custom filter group wrapping {@link TutorFiltersContent}. */
  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  /** Expanded-row colspan (varies with the active tab's column count). */
  const expandedColSpan = getTutorExpandedColSpan(activeTab);

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="tuteurs"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={expandedColSpan}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                if (!dt.showFilters) dt.setShowConfigure(false);
                dt.setShowFilters((prev) => !prev);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des tuteurs en Excel'
              }
            />
          )}
          {showCreateButton && onCreateTutor && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un tuteur"
              onClick={() => {
                onCloseDetails?.();
                onCreateTutor();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={clearFilters}
            resultCount={totalItems}
            resultLabel="tuteur(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => <TutorInPersonTableHead activeTab={activeTab} />}
      renderTableRow={(record: TutorRecord) => (
        <TutorRow
          record={record}
          tabView={activeTab}
          onView={onToggleRow ? () => onToggleRow(record.id) : undefined}
          onEdit={onEditTutor}
          onDelete={(id) => dt.setDeleteId(id)}
          onManageAssignment={handleManageAssignment}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(record: TutorRecord) => (
        <DetailsViewContent>{renderTutorDetails(record)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(record: TutorRecord) => (
        <TutorCard
          record={record}
          tabView={activeTab}
          onView={onToggleRow ? () => onToggleRow(record.id) : undefined}
          onEdit={onEditTutor}
          onDelete={(id) => dt.setDeleteId(id)}
          onManageAssignment={handleManageAssignment}
          pageActions={pageActions}
        />
      )}
      emptyIcon={UserIcon}
      emptyText={
        dt.filters.search?.trim()
          ? 'Aucun tuteur ne correspond à votre recherche'
          : 'Aucun tuteur trouvé'
      }
      title={getRoleLabelPlural('tutor')}
      sidebarMenuId="menu-tutors"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedTutor}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(record: TutorRecord) => (
        <DetailsViewContent>{renderTutorDetails(record)}</DetailsViewContent>
      )}
      modals={
        <>
          <DeleteModal
            open={!!dt.deleteId}
            onClose={() => dt.setDeleteId(null)}
            onConfirm={() => {
              if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
            }}
            itemName={getTutorDeleteItemName(recordToDelete)}
            isLoading={deleteMutation.isPending}
          />
          {assignmentModal && (
            <AssignmentDecisionModal
              open={assignmentModal.open}
              onClose={handleAssignmentModalClose}
              mode={assignmentModal.mode}
              userId={assignmentModal.record.userId}
              companyId={companyId}
              showBothSections={
                pageActions?.canPrevalidate === true && pageActions?.canValidateAssignment === true
              }
              departmentLabel={getTutorDepartments(assignmentModal.record, departments).join(', ')}
              wishes={mapTutorAssignmentWishes(assignmentModal.record)}
              onSuccess={handleAssignmentSuccess}
            />
          )}
          {reassignModal && (
            <ReassignmentModal
              open={reassignModal.open}
              onClose={handleReassignModalClose}
              userId={reassignModal.record.userId}
              companyId={companyId}
              defaultDepartments={getTutorReassignDepartmentIds(reassignModal.record)}
              canDirectAssign={pageActions?.canValidateAssignment === true}
              onSuccess={handleReassignSuccess}
            />
          )}
        </>
      }
    />
  );
};
